import React, { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { KlaviyoBackInStockNotificationModal } from './KlaviyoBackInStockNotificationModal';
import { ViewVariant } from '../../models/ShopifyVariant.model';
import { VariantStatus } from '../../enums/VariantStatus.enum';
import { SubContext } from '../../enums/SubContext.enum';
import { ModalToggleState, DrawerToggleState } from '../../models/actionStates';
import { abTestState, allProductsState } from '../../recoil/atoms';
import { cartDrawerState, cartState } from '../../recoil/selectors';
import { Preorder } from '../../models/Preorder.model';
import { productHandleFromViewVariant, variantStatus } from '../../util';
import { isMobileState } from '../../recoil/selectors';
import { SelectionType } from '../../enums/SelectionType.enum';
import { useAdvanceModalState, useSetModalState } from '../../hooks/actions/useModalState';
import { ModalState } from '../../models/ModalState';
import { DrawerState } from '../../models/DrawerState';
import { ItemForCart, noReturnLineItemPropertyKey, useAddToCartAction } from '../../hooks/actions/useCartState';
import { PreCartModals, usePreCartModal } from '../../hooks/actions/usePreCartModal';
import { ShopifyCart } from '../../models/ShopifyCart.model';
import useSafariBfCacheInvalidator from '../../hooks/useSafariBfCacheInvalidator';
import { HoneyloveAbTests } from '../../models/HoneyloveAbTests';
import { ViewProduct, ViewProducts } from '../../models/ViewProduct.model';
import { useSharedGetPreorderState } from '../../hooks/actions/usePreorderState';
import { useProductSelection } from '../../hooks/useProductSelection';
import { Button, ButtonBaseProps, ButtonStyleProps, ButtonType } from '../../root/components/buttons/Button';
import { openStepper } from './helpers';

interface ProductFormButtonProps extends Omit<ButtonBaseProps, 'text'>, ButtonStyleProps {
  selectionType: SelectionType;
  selectionKey: string;
  quantity?: number;
  beforeSubmit?: () => void;
  afterSubmit?: () => void;
  forceStepper?: boolean;
  properties?: { [key: string]: string };
  disabled?: boolean;
  subContextOverride?: SubContext;
  variantOverride?: ViewVariant;
  productOverride?: ViewProduct;
  disablePreCartModal?: boolean;
  buttonType?: ButtonType;
  text?: string;
}

// eslint-disable-next-line max-lines-per-function
export const ProductFormButton: React.FC<ProductFormButtonProps> = ({
  selectionType,
  selectionKey,
  beforeSubmit,
  afterSubmit,
  forceStepper,
  properties,
  children,
  disabled,
  subContextOverride,
  text: addToBagLabelOverride,
  variantOverride,
  disablePreCartModal,
  quantity = 1,
  buttonType = 'primary',
  size = 'medium',
  fullWidth = true,
}) => {
  const allProducts = useRecoilValue(allProductsState);
  const cart = useRecoilValue(cartState);
  const addToCart = useAddToCartAction();
  const isMobile = useRecoilValue(isMobileState);

  const { variant: stateVariant, subContext: baseSubContext } = useProductSelection(selectionType, selectionKey);
  const variant = variantOverride ?? stateVariant;

  const subContext = subContextOverride ?? baseSubContext;
  const advanceModal = useAdvanceModalState();
  const setModalState = useSetModalState();
  const [cartDrawer, setCartDrawer] = useRecoilState(cartDrawerState);
  const showPreCartModalFn = usePreCartModal(variant, selectionType);
  const abTests = useRecoilValue(abTestState);

  const preorders = useSharedGetPreorderState();
  const findPreorder = (v?: ViewVariant): Preorder =>
    (preorders || []).find((p: Preorder) => p.sku.toLowerCase() === (v?.sku || '').toLowerCase()) || ({} as Preorder);
  const preorder = findPreorder(variant);

  const getVariantStatus = (v: ViewVariant) => {
    return variantStatus(preorder, v);
  };
  const status = variant && getVariantStatus(variant);

  const [loading, setLoading] = useState(false);
  useSafariBfCacheInvalidator(setLoading);
  const addToBagLabel = addToBagLabelOverride ?? 'Add to bag';
  const sharedButtonProps = {
    buttonType,
    size,
    fullWidth,
    loading,
    disabled,
  };

  const defaultReturnEle = (
    <React.Fragment>
      <Button
        {...sharedButtonProps}
        onClick={openStepper({
          advanceModal,
          selectionType,
          selectionKey,
          isMobile,
          quantity,
          subContextOverride: subContext,
          afterSubmit,
          disablePreCartModal,
        })}
        text={disabled ? 'Select a size to continue' : addToBagLabel}
      />
    </React.Fragment>
  );

  if (!variant || !!forceStepper) {
    return defaultReturnEle;
  } else {
    switch (true) {
      case status === VariantStatus.Available: {
        return (
          <React.Fragment>
            <Button
              {...sharedButtonProps}
              text={addToBagLabel}
              onClick={handleSubmit(
                cart,
                addToCart,
                setModalState,
                cartDrawer,
                setCartDrawer,
                variant,
                loading,
                setLoading,
                showPreCartModalFn,
                abTests,
                quantity,
                allProducts,
                disablePreCartModal,
                subContext,
                afterSubmit,
                beforeSubmit,
                properties,
              )}
            />
            {children}
          </React.Fragment>
        );
      }
      case status === VariantStatus.SoldOut: {
        return <KlaviyoBackInStockNotificationModal selectionType={selectionType} selectionKey={selectionKey} buttonType={buttonType} size={size} />;
      }
      case status === VariantStatus.PreOrder: {
        const preOrderProperties = preorder?.date ? { _preorder: preorder?.date } : {};
        return (
          <Button
            {...sharedButtonProps}
            text={preorder?.ctaText ?? 'Add to bag'}
            subText={preorder?.preorderText}
            onClick={handleSubmit(
              cart,
              addToCart,
              setModalState,
              cartDrawer,
              setCartDrawer,
              variant,
              loading,
              setLoading,
              showPreCartModalFn,
              abTests,
              quantity,
              allProducts,
              disablePreCartModal,
              subContext,
              afterSubmit,
              beforeSubmit,
              preOrderProperties,
            )}
          />
        );
      }
      default: {
        return defaultReturnEle;
      }
    }
  }
};

const handleSubmit =
  (
    cart: ShopifyCart,
    addToCart: (items: ItemForCart[], subContext: SubContext) => Promise<ShopifyCart>,
    setModalState: (modalState: ModalState) => void,
    cartDrawer: DrawerState,
    setCartDrawer: (state: DrawerState) => void,
    variant: ViewVariant,
    loading: boolean,
    setLoading: (value: boolean) => void,
    showPreCartModal: (modalType: PreCartModals) => void,
    abTests: HoneyloveAbTests,
    quantity: number,
    allProducts: ViewProducts,
    disablePreCartModal?: boolean,
    subContext?: SubContext,
    afterSubmit?: () => void,
    beforeSubmit?: () => void,
    properties = {},
  ) =>
  async () => {
    if (!loading) {
      setLoading(true);
      const mergedProperties = abTests['AB2021-13'] ? { ...properties, _AB2021_13: true } : properties;
      let updatedCart = cart;

      // use the cart drawer unless on the cart page
      beforeSubmit?.();
      updatedCart = await addToCart(
        [{ productHandle: productHandleFromViewVariant(variant, allProducts), variant, quantity, properties: mergedProperties }],
        subContext ?? SubContext.ProductFormButton,
      );
      setCartDrawer({ ...cartDrawer, toggleState: DrawerToggleState.Open });
      afterSubmit?.();

      const warehouseItemCount =
        updatedCart.lineItems
          ?.filter((li) => li.properties[noReturnLineItemPropertyKey])
          .reduce((acc, curr) => {
            return acc + curr.quantity;
          }, 0) ?? 0;
      const itemCount = updatedCart ? (updatedCart.itemCount ?? 0) - warehouseItemCount : 0;
      if (itemCount === 1 && !disablePreCartModal) {
        showPreCartModal(PreCartModals.ColorUpsell);
      } else {
        // close modal
        setModalState({ toggleState: ModalToggleState.Closed });
      }
      setLoading(false);
    }
  };

export default ProductFormButton;
